import React, { useEffect, useState, useCallback } from 'react';
import { Bell, Check } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABEL = {
    access_request: 'Nouvelle demande',
    meeting_started: 'Réunion démarrée',
    approval_requested: 'Validation requise',
    approval_pending: 'Validation en attente',
    invoice_due: 'Facture à payer',
    invoice_overdue: 'Facture en retard',
    mention: '@Mention reçue',
    donation_received: 'Don reçu',
    contact_message: 'Message contact',
    system: 'Système',
};

export default function NotificationBell() {
    const { user, session } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);

    const authHeader = useCallback(() => session?.access_token
        ? { headers: { Authorization: `Bearer ${session.access_token}` } }
        : null, [session]);

    const load = useCallback(async () => {
        const h = authHeader();
        if (!h) return;
        try {
            const r = await axios.get(`${API}/notifications`, h);
            setItems(r.data.items || []);
            setUnread(r.data.unread || 0);
        } catch { /* silent */ }
    }, [authHeader]);

    useEffect(() => {
        if (!user) return;
        load();
        // Background scan to surface pending validations & invoices due
        const h = authHeader();
        if (h) {
            axios.post(`${API}/notifications/scan`, {}, h)
                .then(() => load())
                .catch(() => { /* silent */ });
        }
        const channel = supabase
            .channel(`notif:${user.id}`)
            .on('postgres_changes',
                { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
                (payload) => {
                    setItems(prev => [payload.new, ...prev].slice(0, 30));
                    setUnread(u => u + 1);
                })
            .on('postgres_changes',
                { event: 'UPDATE', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
                (payload) => {
                    setItems(prev => prev.map(it => it.id === payload.new.id ? payload.new : it));
                    setUnread(u => {
                        if (payload.old?.is_read === false && payload.new?.is_read === true) return Math.max(0, u - 1);
                        return u;
                    });
                })
            .subscribe();
        // Light polling fallback every 60s in case Realtime is off in Supabase
        const t = setInterval(load, 60000);
        return () => { supabase.removeChannel(channel); clearInterval(t); };
    }, [user, load]);

    const openNotif = async (n) => {
        const h = authHeader();
        if (h && !n.is_read) {
            try { await axios.post(`${API}/notifications/${n.id}/read`, {}, h); } catch { /* silent */ }
            setItems(prev => prev.map(it => it.id === n.id ? { ...it, is_read: true } : it));
            setUnread(u => Math.max(0, u - 1));
        }
        if (n.link) navigate(n.link);
    };

    const markAllRead = async () => {
        const h = authHeader();
        if (!h) return;
        try {
            await axios.post(`${API}/notifications/mark-all-read`, {}, h);
            setItems(prev => prev.map(it => ({ ...it, is_read: true })));
            setUnread(0);
        } catch { /* silent */ }
    };

    if (!user) return null;

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button type="button" className="relative h-9 w-9 rounded-md hover:bg-surface-muted transition-colors flex items-center justify-center" data-testid="notif-bell">
                    <Bell size={16} strokeWidth={1.6} />
                    {unread > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center" data-testid="notif-count">{unread > 9 ? '9+' : unread}</span>
                    )}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-96">
                <div className="flex items-center justify-between px-2 py-1.5">
                    <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground p-0">Notifications</DropdownMenuLabel>
                    {unread > 0 && (
                        <button
                            type="button"
                            onClick={markAllRead}
                            className="text-[11px] text-primary hover:underline inline-flex items-center gap-1"
                            data-testid="notif-mark-all-read"
                        >
                            <Check size={12} /> Tout marquer lu
                        </button>
                    )}
                </div>
                <DropdownMenuSeparator />
                {items.length === 0 && <DropdownMenuItem disabled>Aucune notification.</DropdownMenuItem>}
                {items.slice(0, 15).map(n => (
                    <DropdownMenuItem
                        key={n.id}
                        onClick={() => openNotif(n)}
                        className={`flex-col items-start py-2.5 cursor-pointer ${!n.is_read ? 'bg-primary-light/40' : ''}`}
                        data-testid={`notif-${n.id}`}
                    >
                        <div className="flex items-center justify-between w-full gap-2">
                            <div className="text-sm font-medium truncate">{n.title}</div>
                            {!n.is_read && <span className="h-2 w-2 rounded-full bg-primary shrink-0" />}
                        </div>
                        {n.body && <div className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{n.body}</div>}
                        <div className="text-[10px] text-muted-foreground/70 mt-1">
                            {TYPE_LABEL[n.type] || n.type} · {new Date(n.created_at).toLocaleString()}
                        </div>
                    </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                    onClick={() => navigate('/app/notifications')}
                    className="justify-center text-xs text-primary cursor-pointer hover:bg-primary-light/30"
                    data-testid="notif-see-all"
                >
                    Voir toutes les notifications →
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
